import { useEffect, useMemo, useState } from 'react';
import { api, type Bench, type ModelInfo } from '../lib/api';
import { filterModels, readFavs, sayPrice, sortModels, toggleFav, type SortKey } from '../lib/models';
import { ModelInfoPanel } from './ModelInfo';

// ---------------------------------------------------------------------------
// The catalogue (205): every model on every bench, readable without opening
// a bench. The same rows the picker shows, with the ⓘ that opens the info
// panel, a search box and the picker's sorts and filters. Reachable in both
// modes under Workspace, from Settings → Models, and from the Credits page,
// where a model's name in the tables lands here with its panel open.
// ---------------------------------------------------------------------------

const BENCHES: Array<{ bench: Bench; label: string; word: string }> = [
  { bench: 'image', label: 'Pictures', word: 'A picture from a prompt.' },
  { bench: 'edit', label: 'Edits', word: 'A redraw, a combine, a cutout or an enlargement of a picture you have.' },
  { bench: 'video', label: 'Shots', word: 'A generated video shot, from words or from a picture.' },
  { bench: 'music', label: 'Music', word: 'A music bed.' },
  { bench: 'text', label: 'The director', word: 'The writing model behind storylines, hooks, research and every proposal.' },
  { bench: 'tts', label: 'Voices', word: 'Narration, priced per thousand characters.' },
  { bench: 'upscale', label: 'Upscale', word: 'A picture made larger.' },
  { bench: 'stt', label: 'Transcription', word: 'Speech to text, priced per second.' },
];

export function Catalogue({ open, onOpened }: { open?: { bench?: string; modelId?: string } | null; onOpened?: () => void }) {
  const [bench, setBench] = useState<Bench>((open?.bench as Bench) ?? 'image');
  const [lists, setLists] = useState<Partial<Record<Bench, ModelInfo[] | null>>>({});
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [sort, setSort] = useState<SortKey>('name');
  const [privacy, setPrivacy] = useState<'private' | 'anonymized' | null>(null);
  const [favsOnly, setFavsOnly] = useState(false);
  const [favs, setFavs] = useState<string[]>(() => readFavs());
  const [info, setInfo] = useState<ModelInfo | null>(null);

  useEffect(() => {
    if (lists[bench] !== undefined) return;
    let live = true;
    setLists((l) => ({ ...l, [bench]: null }));
    api.models(bench).then((m) => { if (live) setLists((l) => ({ ...l, [bench]: m })); })
      .catch((e: Error) => { if (live) { setError(e.message); setLists((l) => ({ ...l, [bench]: [] })); } });
    return () => { live = false; };
  }, [bench]); // eslint-disable-line react-hooks/exhaustive-deps

  // Landed here from the Credits page with a model named: open its panel once the list is in.
  useEffect(() => {
    if (!open?.modelId) return;
    const list = lists[open.bench as Bench ?? bench];
    if (!list) return;
    const m = list.find((x) => x.id === open.modelId);
    if (m) setInfo(m);
    onOpened?.();
  }, [lists, open]); // eslint-disable-line react-hooks/exhaustive-deps

  const list = lists[bench] ?? null;
  const shown = useMemo(() => sortModels(filterModels(list ?? [], { q, privacy, favsOnly, favs }), sort), [list, q, privacy, favsOnly, favs, sort]);
  const here = BENCHES.find((b) => b.bench === bench)!;
  const privacyWord = (p: ModelInfo['privacy']) => (p === 'private' ? 'Private' : p === 'anonymized' ? 'Anon' : null);

  return (
    <>
      <div className="vhead">
        <div>
          <h2>Models</h2>
          <p>Every model this workspace can press, by bench, with what it costs and what it can do. ⓘ opens the whole story.</p>
        </div>
      </div>
      {error && <div className="err">{error}</div>}
      <section className="card" data-tool="catalogue">
        <div className="catalogue-tabs" role="tablist" aria-label="Benches">
          {BENCHES.map((b) => (
            <button key={b.bench} type="button" role="tab" aria-selected={b.bench === bench} className={`chip${b.bench === bench ? ' on' : ''}`} onClick={() => { setBench(b.bench); setQ(''); }}>{b.label}</button>
          ))}
        </div>
        <p className="sub-dim" style={{ margin: 0 }}>{here.word}{list ? ` ${list.length} model${list.length === 1 ? '' : 's'}.` : ''}</p>
        <div className="picker-bar">
          <input className="picker-search" data-tool="catalogue-search" type="search" placeholder="Search models…" value={q} onChange={(e) => setQ(e.target.value)} style={{ flex: '1 1 200px', minWidth: 0 }} />
          <button type="button" className={`chip${favsOnly ? ' on' : ''}`} title="Only your favourites" onClick={() => setFavsOnly((f) => !f)}>★ {favs.length}</button>
          <select value={sort} aria-label="Sort" onChange={(e) => setSort(e.target.value as SortKey)}>
            <option value="name">Name</option><option value="price">Price</option><option value="new">New first</option>
          </select>
          <select value={privacy ?? ''} aria-label="Privacy" onChange={(e) => setPrivacy((e.target.value || null) as 'private' | 'anonymized' | null)}>
            <option value="">Any privacy</option><option value="private">Private</option><option value="anonymized">Anon</option>
          </select>
        </div>
        {!list ? <p className="sub-dim">Reading the catalogue…</p> : (
          <ul className="catalogue-list" aria-label={`${here.label} models`}>
            {shown.length === 0 && <li className="picker-empty">Nothing matches.</li>}
            {shown.map((m) => {
              const gone = m.status === 'deprecated';
              return (
                <li key={m.id} className={`picker-row picker-card${gone ? ' gone' : ''}`}>
                  <span className="picker-mark" aria-hidden="true">{m.vendor.slice(0, 1).toUpperCase()}</span>
                  <span className="picker-main">
                    <span className="picker-row-name">{m.name}</span>
                    <span className="picker-badges">
                      {privacyWord(m.privacy) && <span className={`picker-badge ${m.privacy}`}>{privacyWord(m.privacy)}</span>}
                      {m.isNew && <span className="picker-badge new">New</span>}
                      {m.featured && <span className="picker-badge">Featured</span>}
                      {m.houseDefault && <span className="picker-badge">House</span>}
                      {m.workspaceDefault && <span className="picker-badge">Yours</span>}
                      {gone && <span className="picker-badge gone">{m.replacement ? `→ ${m.replacement.name}` : 'Going away'}</span>}
                    </span>
                    {m.blurb && <span className="picker-blurb">{m.blurb}</span>}
                  </span>
                  <span className="picker-price">{sayPrice(m.credits, m.unit)}</span>
                  <button type="button" className="picker-info" aria-label={`About ${m.name}`} title="About this model" onClick={() => setInfo(m)}>ⓘ</button>
                  <button type="button" className={`picker-fav${favs.includes(m.id) ? ' on' : ''}`} aria-label={favs.includes(m.id) ? 'Unfavourite' : 'Favourite'} title="Favourite"
                          onClick={() => setFavs(toggleFav(m.id))}>★</button>
                </li>
              );
            })}
          </ul>
        )}
      </section>
      {info && <ModelInfoPanel bench={bench} model={info} onClose={() => setInfo(null)} />}
    </>
  );
}
